export type MarketingPicture = {
  sources: Record<string, string>;
  img: {
    src: string;
    w?: number;
    h?: number;
    width?: number;
    height?: number;
  };
};

export const HERO_IMAGE_SIZES = "100vw";
export const CONTENT_IMAGE_SIZES = "(min-width: 768px) 50vw, 100vw";

const FORMAT_PRIORITY = ["avif", "webp", "jpg", "jpeg"] as const;

/**
 * vite-imagetools may emit JPEG URLs as `.jpeg` (Sharp metadata) while the
 * asset on disk is `.jpg` (`format=jpg`, or a cold-vs-warm cache mismatch
 * across client/SSR builds). Always prefer `.jpg` in served URLs.
 */
export function asJpgAssetUrl(value: string): string {
  return value.replace(/\.jpeg\b/g, ".jpg");
}

function mimeForImageFormat(format: string): string {
  if (format === "jpg" || format === "jpeg") return "image/jpeg";
  return `image/${format}`;
}

/** Prefer AVIF (then WebP) srcset for LCP preload. */
export function preferredHeroSrcSet(picture: MarketingPicture): {
  srcSet: string;
  type: string;
} | null {
  for (const format of FORMAT_PRIORITY) {
    const srcSet = picture.sources[format];
    if (srcSet) {
      return { srcSet: asJpgAssetUrl(srcSet), type: mimeForImageFormat(format) };
    }
  }
  return null;
}

export function pictureSourceEntries(picture: MarketingPicture): Array<{
  type: string;
  srcSet: string;
}> {
  const entries: Array<{ type: string; srcSet: string }> = [];
  for (const format of FORMAT_PRIORITY) {
    const srcSet = picture.sources[format];
    if (srcSet) {
      entries.push({ type: mimeForImageFormat(format), srcSet: asJpgAssetUrl(srcSet) });
    }
  }
  for (const [format, srcSet] of Object.entries(picture.sources)) {
    if ((FORMAT_PRIORITY as readonly string[]).includes(format)) continue;
    entries.push({ type: mimeForImageFormat(format), srcSet: asJpgAssetUrl(srcSet) });
  }
  return entries;
}

/** Fallback `<img src>` with JPEG extension normalized to `.jpg`. */
export function pictureImgSrc(picture: MarketingPicture): string {
  return asJpgAssetUrl(picture.img.src);
}

export function heroPreloadLink(
  picture: MarketingPicture,
  sizes: string = HERO_IMAGE_SIZES,
): {
  rel: "preload";
  as: "image";
  type?: string;
  imageSrcSet: string;
  imageSizes: string;
  fetchPriority: "high";
} {
  const preferred = preferredHeroSrcSet(picture);
  return {
    rel: "preload",
    as: "image",
    ...(preferred?.type ? { type: preferred.type } : {}),
    imageSrcSet: preferred?.srcSet ?? pictureImgSrc(picture),
    imageSizes: sizes,
    fetchPriority: "high",
  };
}

export function pictureDimensions(picture: MarketingPicture): {
  width?: number;
  height?: number;
} {
  const width = picture.img.w ?? picture.img.width;
  const height = picture.img.h ?? picture.img.height;
  return {
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
  };
}
